import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  Divider,
} from '@mui/material';
import { Save, Description, Refresh } from '@mui/icons-material';
import { Percent, DollarSign } from 'lucide-react';
import { getBillingSettings, saveBillingSettings } from '../firebase/settingsService';

const BillingSettings = () => {
  const [settings, setSettings] = useState({
    companyName: 'Perfume Stories',
    companyAddress: '123 Business Street, City, State 12345',
    companyPhone: '+1 (234) 567-8900',
    companyEmail: 'info@perfumestories.com',
    taxRate: '10',
    currency: 'LKR',
    invoicePrefix: 'INV',
    companyLogo: '', // Logo URL
    includeCompanyLogo: true,
    includeTermsAndConditions: true,
    includePaymentInstructions: true,
    includeTaxBreakdown: true,
    includeDiscountField: true,
    includeNotes: true,
    termsText: 'Payment is due within 30 days. Late payments may incur additional charges.',
    paymentInstructions: 'Please make payment via bank transfer or credit card.',
    globalDiscount: {
      enabled: false,
      type: 'percentage', // 'percentage' or 'fixed'
      value: 0,
      reason: '',
    },
    loyaltyDiscount: {
      enabled: true,
      discountType: 'percentage',
      discountValue: 5,
      minOrdersRequired: 1,
      message: 'Loyalty Discount Applied! Thank you for being a valued customer.',
      tiers: {
        bronze: {
          minOrders: 1,
          maxOrders: 9,
          bonusDiscount: 0
        },
        silver: {
          minOrders: 10,
          maxOrders: 19,
          bonusDiscount: 1
        },
        gold: {
          minOrders: 20,
          maxOrders: 49,
          bonusDiscount: 2
        },
        platinum: {
          minOrders: 50,
          maxOrders: 999999,
          bonusDiscount: 3
        }
      }
    },
    kokoChargePercentage: 12, // Default 12% Koko service charge
    mintPayChargePercentage: 12, // Default 12% MintPay service charge
    defaultDeliveryCharge: 450, // Default COD delivery charge
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const result = await getBillingSettings();
    if (result.success) {
      setSettings(result.data);
    } else {
      showSnackbar(result.error || 'Failed to load settings', 'error');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadSettings();
    setRefreshing(false);
    showSnackbar('Settings refreshed');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleGlobalDiscountChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      globalDiscount: {
        ...prev.globalDiscount,
        [field]: value
      }
    }));
  };

  const handleLoyaltyDiscountChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      loyaltyDiscount: {
        ...prev.loyaltyDiscount,
        [field]: value
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const result = await saveBillingSettings(settings);
    if (result.success) {
      showSnackbar('Settings saved successfully!');
    } else {
      showSnackbar(result.error || 'Failed to save settings', 'error');
    }
    setSaving(false);
  };

  const handleSaveTier = async (tierName) => {
    setSaving(true);
    const result = await saveBillingSettings(settings);
    if (result.success) {
      showSnackbar(`${tierName.charAt(0).toUpperCase() + tierName.slice(1)} tier saved successfully!`);
    } else {
      showSnackbar(result.error || 'Failed to save tier', 'error');
    }
    setSaving(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        showSnackbar('Logo file size should be less than 2MB', 'error');
        return;
      }
      
      setUploadingLogo(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({ ...settings, companyLogo: reader.result });
        setUploadingLogo(false);
        showSnackbar('Logo uploaded successfully');
      };
      reader.onerror = () => {
        setUploadingLogo(false);
        showSnackbar('Failed to upload logo', 'error');
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Billing Settings
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Customize your invoice templates and billing preferences
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Company Information
              </Typography>
              
              {/* Logo Upload */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Company Logo
                </Typography>
                {settings.companyLogo && (
                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                    <img 
                      src={settings.companyLogo} 
                      alt="Company Logo" 
                      style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                    />
                  </Box>
                )}
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? 'Uploading...' : settings.companyLogo ? 'Change Logo' : 'Upload Logo'}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Recommended: PNG or JPG, max 2MB
                </Typography>
              </Box>

              <TextField
                fullWidth
                label="Company Name"
                name="companyName"
                value={settings.companyName}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Company Address"
                name="companyAddress"
                value={settings.companyAddress}
                onChange={handleChange}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                name="companyPhone"
                value={settings.companyPhone}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email Address"
                name="companyEmail"
                type="email"
                value={settings.companyEmail}
                onChange={handleChange}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Invoice Settings
              </Typography>
              <TextField
                fullWidth
                label="Tax Rate (%)"
                name="taxRate"
                type="number"
                value={settings.taxRate}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Currency</InputLabel>
                <Select
                  name="currency"
                  value={settings.currency}
                  label="Currency"
                  onChange={handleChange}
                >
                  <MenuItem value="LKR">LKR - Sri Lankan Rupee</MenuItem>
                  <MenuItem value="USD">USD - US Dollar</MenuItem>
                  <MenuItem value="EUR">EUR - Euro</MenuItem>
                  <MenuItem value="GBP">GBP - British Pound</MenuItem>
                  <MenuItem value="INR">INR - Indian Rupee</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Invoice Prefix"
                name="invoicePrefix"
                value={settings.invoicePrefix}
                onChange={handleChange}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Description sx={{ mr: 1 }} color="action" />
            <Typography variant="h6" fontWeight={600}>
              Invoice Components
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeCompanyLogo"
                    checked={settings.includeCompanyLogo}
                    onChange={handleChange}
                  />
                }
                label="Include Company Logo"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeTaxBreakdown"
                    checked={settings.includeTaxBreakdown}
                    onChange={handleChange}
                  />
                }
                label="Include Tax Breakdown"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeDiscountField"
                    checked={settings.includeDiscountField}
                    onChange={handleChange}
                  />
                }
                label="Include Discount Field"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeNotes"
                    checked={settings.includeNotes}
                    onChange={handleChange}
                  />
                }
                label="Include Notes Section"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includeTermsAndConditions"
                    checked={settings.includeTermsAndConditions}
                    onChange={handleChange}
                  />
                }
                label="Include Terms & Conditions"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="includePaymentInstructions"
                    checked={settings.includePaymentInstructions}
                    onChange={handleChange}
                  />
                }
                label="Include Payment Instructions"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {settings.includeTermsAndConditions && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Terms and Conditions
            </Typography>
            <TextField
              fullWidth
              name="termsText"
              value={settings.termsText}
              onChange={handleChange}
              multiline
              rows={4}
            />
          </CardContent>
        </Card>
      )}

      {settings.includePaymentInstructions && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Payment Instructions
            </Typography>
            <TextField
              fullWidth
              name="paymentInstructions"
              value={settings.paymentInstructions}
              onChange={handleChange}
              multiline
              rows={3}
            />
          </CardContent>
        </Card>
      )}

      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Percent size={24} color="primary" />
              <Typography variant="h6" fontWeight={600}>
                Global Discount (All Products)
              </Typography>
            </Box>
            <Switch
              checked={settings.globalDiscount?.enabled || false}
              onChange={(e) => handleGlobalDiscountChange('enabled', e.target.checked)}
              size="medium"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {settings.globalDiscount?.enabled && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                This discount will be applied to all products in billing. Current discount: 
                <strong>
                  {(settings.globalDiscount?.type === 'percentage') 
                    ? ` ${settings.globalDiscount?.value}%` 
                    : ` ${settings.currency} ${settings.globalDiscount?.value}`}
                </strong>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Discount Type</InputLabel>
                    <Select
                      value={settings.globalDiscount?.type || 'percentage'}
                      label="Discount Type"
                      onChange={(e) => handleGlobalDiscountChange('type', e.target.value)}
                    >
                      <MenuItem value="percentage">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Percent size={18} /> Percentage (%)
                        </Box>
                      </MenuItem>
                      <MenuItem value="fixed">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DollarSign size={18} /> Fixed Amount ({settings.currency})
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={(settings.globalDiscount?.type === 'percentage') ? 'Discount %' : `Discount (${settings.currency})`}
                    type="number"
                    inputProps={{ 
                      step: (settings.globalDiscount?.type === 'percentage') ? '0.1' : '1',
                      min: '0',
                      max: (settings.globalDiscount?.type === 'percentage') ? '100' : undefined
                    }}
                    value={settings.globalDiscount?.value || 0}
                    onChange={(e) => handleGlobalDiscountChange('value', parseFloat(e.target.value) || 0)}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Discount Reason (Optional)"
                    placeholder="e.g., Seasonal Sale, Bulk Order, Loyalty Program"
                    value={settings.globalDiscount?.reason || ''}
                    onChange={(e) => handleGlobalDiscountChange('reason', e.target.value)}
                    helperText="This helps track why the discount was applied"
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Example:</strong> If you set 10% discount, every product price will be reduced by 10% in billing.
                </Typography>
              </Box>
            </Box>
          )}

          {!settings.globalDiscount?.enabled && (
            <Typography variant="body2" color="text.secondary">
              Enable the toggle above to apply a discount to all products.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Loyalty Discount Settings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                Loyalty Discount (Returning Customers)
              </Typography>
            </Box>
            <Switch
              checked={settings.loyaltyDiscount?.enabled !== false}
              onChange={(e) => handleLoyaltyDiscountChange('enabled', e.target.checked)}
              size="medium"
            />
          </Box>

          <Divider sx={{ mb: 2 }} />

          {settings.loyaltyDiscount?.enabled !== false && (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Loyalty discount will automatically appear for customers who have made{' '}
                <strong>{settings.loyaltyDiscount?.minOrdersRequired || 1}</strong> or more previous purchases.
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Minimum Orders Required"
                    type="number"
                    inputProps={{ min: '1', step: '1' }}
                    value={settings.loyaltyDiscount?.minOrdersRequired || 1}
                    onChange={(e) => handleLoyaltyDiscountChange('minOrdersRequired', parseInt(e.target.value) || 1)}
                    helperText="Number of previous orders needed to qualify"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Loyalty Message"
                    placeholder="Thank you for being a valued customer!"
                    value={settings.loyaltyDiscount?.message || 'Loyalty Discount Applied! Thank you for being a valued customer.'}
                    onChange={(e) => handleLoyaltyDiscountChange('message', e.target.value)}
                    helperText="Message shown when discount is applied"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: 'success.lighter', borderRadius: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Loyalty Tiers Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.875rem' }}>
                      Configure tier thresholds and discount percentages. Each tier's discount is applied automatically based on customer order count.
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {/* Bronze Tier */}
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '2px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom color="text.primary">
                            Bronze Tier
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Min Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.bronze?.minOrders || 1}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  bronze: { ...settings.loyaltyDiscount?.tiers?.bronze, minOrders: parseInt(e.target.value) || 1 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Max Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.bronze?.maxOrders || 9}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  bronze: { ...settings.loyaltyDiscount?.tiers?.bronze, maxOrders: parseInt(e.target.value) || 9 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Discount %"
                                type="number"
                                inputProps={{ min: '0', step: '0.1', max: '100' }}
                                value={settings.loyaltyDiscount?.tiers?.bronze?.bonusDiscount || 0}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  bronze: { ...settings.loyaltyDiscount?.tiers?.bronze, bonusDiscount: parseFloat(e.target.value) || 0 }
                                })}
                                helperText="Discount percentage for this tier"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveTier('bronze')}
                                disabled={saving}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                              >
                                {saving ? 'Saving...' : 'Save Bronze Tier'}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {/* Silver Tier */}
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '2px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom color="text.primary">
                            Silver Tier
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Min Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.silver?.minOrders || 10}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  silver: { ...settings.loyaltyDiscount?.tiers?.silver, minOrders: parseInt(e.target.value) || 10 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Max Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.silver?.maxOrders || 19}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  silver: { ...settings.loyaltyDiscount?.tiers?.silver, maxOrders: parseInt(e.target.value) || 19 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Discount %"
                                type="number"
                                inputProps={{ min: '0', step: '0.1', max: '100' }}
                                value={settings.loyaltyDiscount?.tiers?.silver?.bonusDiscount || 1}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  silver: { ...settings.loyaltyDiscount?.tiers?.silver, bonusDiscount: parseFloat(e.target.value) || 1 }
                                })}
                                helperText="Discount percentage for this tier"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveTier('silver')}
                                disabled={saving}
                                sx={{ bgcolor: '#C0C0C0', color: '#000', '&:hover': { bgcolor: '#A8A8A8' } }}
                              >
                                {saving ? 'Saving...' : 'Save Silver Tier'}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {/* Gold Tier */}
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '2px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom color="text.primary">
                            Gold Tier
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Min Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.gold?.minOrders || 20}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  gold: { ...settings.loyaltyDiscount?.tiers?.gold, minOrders: parseInt(e.target.value) || 20 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Max Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.gold?.maxOrders || 49}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  gold: { ...settings.loyaltyDiscount?.tiers?.gold, maxOrders: parseInt(e.target.value) || 49 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Discount %"
                                type="number"
                                inputProps={{ min: '0', step: '0.1', max: '100' }}
                                value={settings.loyaltyDiscount?.tiers?.gold?.bonusDiscount || 2}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  gold: { ...settings.loyaltyDiscount?.tiers?.gold, bonusDiscount: parseFloat(e.target.value) || 2 }
                                })}
                                helperText="Discount percentage for this tier"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveTier('gold')}
                                disabled={saving}
                                sx={{ bgcolor: '#FFD700', color: '#000', '&:hover': { bgcolor: '#E6C200' } }}
                              >
                                {saving ? 'Saving...' : 'Save Gold Tier'}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>

                      {/* Platinum Tier */}
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '2px solid', borderColor: 'divider' }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom color="text.primary">
                            Platinum Tier
                          </Typography>
                          <Grid container spacing={1.5}>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Min Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.platinum?.minOrders || 50}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  platinum: { ...settings.loyaltyDiscount?.tiers?.platinum, minOrders: parseInt(e.target.value) || 50 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={6}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Max Orders"
                                type="number"
                                inputProps={{ min: '1', step: '1' }}
                                value={settings.loyaltyDiscount?.tiers?.platinum?.maxOrders || 999999}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  platinum: { ...settings.loyaltyDiscount?.tiers?.platinum, maxOrders: parseInt(e.target.value) || 999999 }
                                })}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                size="small"
                                label="Discount %"
                                type="number"
                                inputProps={{ min: '0', step: '0.1', max: '100' }}
                                value={settings.loyaltyDiscount?.tiers?.platinum?.bonusDiscount || 3}
                                onChange={(e) => handleLoyaltyDiscountChange('tiers', {
                                  ...settings.loyaltyDiscount?.tiers,
                                  platinum: { ...settings.loyaltyDiscount?.tiers?.platinum, bonusDiscount: parseFloat(e.target.value) || 3 }
                                })}
                                helperText="Discount percentage for this tier"
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Button
                                fullWidth
                                variant="contained"
                                size="small"
                                onClick={() => handleSaveTier('platinum')}
                                disabled={saving}
                                sx={{ bgcolor: '#E5E4E2', color: '#000', '&:hover': { bgcolor: '#D0CFCD' } }}
                              >
                                {saving ? 'Saving...' : 'Save Platinum Tier'}
                              </Button>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        <strong>How it works:</strong> When a customer is selected in billing, their tier is automatically determined 
                        by their total order count. The discount percentage for their tier is applied when the cashier clicks "Apply Loyalty Discount".
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {settings.loyaltyDiscount?.enabled === false && (
            <Typography variant="body2" color="text.secondary">
              Enable the toggle above to reward returning customers with automatic loyalty discounts.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Percent size={24} color="orange" />
            <Typography variant="h6" fontWeight={600}>
              💳 Koko Payment Service Charge
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            This percentage will be added to the total bill amount for all Koko payment type purchases. 
            Current charge: <strong>{settings.kokoChargePercentage || 12}%</strong>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Koko Service Charge %"
                type="number"
                inputProps={{ 
                  step: '0.1',
                  min: '0',
                  max: '100'
                }}
                value={settings.kokoChargePercentage || 12}
                onChange={(e) => setSettings({ ...settings, kokoChargePercentage: parseFloat(e.target.value) || 12 })}
                helperText="Percentage added to total for Koko payments (default: 12%)"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Example:</strong> If bill total is LKR 10,000 and Koko charge is 12%, 
              the customer will pay LKR 11,200 (10,000 + 1,200 service charge).
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Percent size={24} color="orange" />
            <Typography variant="h6" fontWeight={600}>
              💳 MintPay Payment Service Charge
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            This percentage will be added to the total bill amount for all MintPay payment type purchases. 
            Current charge: <strong>{settings.mintPayChargePercentage || 12}%</strong>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="MintPay Service Charge %"
                type="number"
                inputProps={{ 
                  step: '0.1',
                  min: '0',
                  max: '100'
                }}
                value={settings.mintPayChargePercentage || 12}
                onChange={(e) => setSettings({ ...settings, mintPayChargePercentage: parseFloat(e.target.value) || 12 })}
                helperText="Percentage added to total for MintPay payments (default: 12%)"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Example:</strong> If bill total is LKR 10,000 and MintPay charge is 12%, 
              the customer will pay LKR 11,200 (10,000 + 1,200 service charge).
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* COD Delivery Charge Settings */}
      <Card sx={{ mb: 3, border: '2px solid', borderColor: 'info.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <DollarSign size={24} />
            <Typography variant="h6" fontWeight={600}>
              COD Delivery Charge
            </Typography>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Alert severity="info" sx={{ mb: 2 }}>
            This is the default delivery charge pre-filled when COD payment is selected.
            Cashier can edit or clear it per bill. Current default: <strong>LKR {settings.defaultDeliveryCharge ?? 450}</strong>
          </Alert>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Default Delivery Charge (LKR)"
                type="number"
                inputProps={{ step: '1', min: '0' }}
                value={settings.defaultDeliveryCharge ?? 450}
                onChange={(e) => setSettings({ ...settings, defaultDeliveryCharge: parseFloat(e.target.value) || 0 })}
                helperText="Pre-filled amount for COD orders. Can be edited or set to 0 per bill."
                InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>LKR</Typography> }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Note:</strong> Delivery charge is optional per bill. Cashier can change or remove it.
              Delivery income is tracked separately from product revenue in reports.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        size="large"
        startIcon={saving ? <CircularProgress size={20} /> : <Save />}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BillingSettings;
